import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { ProductModelType } from '@teinor/erp/types/company/product'
import { useCallback, useState } from 'react'
import { ComponentWithPermissions } from '../../../../../baseComponents/CreateRoutering/types'
import TwinIcon from '../../../../../baseComponents/TwinIcon'
import TwinTrans from '../../../../../baseComponents/TwinTrans'
import { useParams } from 'react-router-dom'
import SearchBarProduct from '../../../SearchBarProduct'
import { dictionaryComplexFromJsonArr } from '../../../../../utils/globals/dictionary'
import PermissionChecker from '../../../../../baseComponents/PermissionChecker'
import { ProductExtendedTyShort, ProductExtendedTyShortDict } from '../../../types'
import { OnChangeSearchBarAddProduct } from '../../../SearchBarProduct/types'
import { addAntiUnsaveElement } from '../../../../../utils/reducers/reduxDispatch'


interface RelatedProductsProps extends RelatedProductsLogicProps {}

const RelatedProducts: React.FC<RelatedProductsProps & ComponentWithPermissions> = ({ relatedProducts, ProductId, userPermissions }) => {
    const { id, data, changeData, setData } = useRelatedProductsLogic({ ProductId, relatedProducts })
    return (
        <div className='relative'>
            <span className='block regular18 mb-22'>
                <TwinTrans transKey='relatedProducts'>Productos relacionados</TwinTrans>
            </span>
            <PermissionChecker userPermissions={userPermissions} permission={'create'}>
                <SearchBarProduct changeSelectedItems={changeData} excludeIds={Object.keys(data)} id={id || ''} fieldNotIn='Product.id' name='ProductsRelated' url='/api/app/product/getAllProducts' where={{ 'productType': { '$not': [5] } }} />
            </PermissionChecker>
            <RenderRelatedProducts {...{ setData, userPermissions, data: data || {} }} />
            <input value={Object.keys(data).join(';')} type='hidden' name='relatedProducts' />
        </div>
    )
}

interface RelatedProductsLogicProps {
    ProductId?: number
    relatedProducts?: ProductModelType[]
}

export const useRelatedProductsLogic = ({ relatedProducts }: RelatedProductsLogicProps) => {
    const [data, setData] = useState<ProductExtendedTyShortDict>(() => {
        return dictionaryComplexFromJsonArr(relatedProducts || [])
    })
    const params = useParams<{ id: string }>()
    const id = params.id

    const changeData: OnChangeSearchBarAddProduct = useCallback((item) => {
        setData((oldData) => {
            if (!oldData) {
                return oldData
            }
            const copy = JSON.parse(JSON.stringify(oldData))
            copy[item.id] = item
            return copy
        })
        addAntiUnsaveElement(['relatedProduct', {}])
    }, [setData])

    return {id, data, changeData, setData }
}

interface RenderRelatedProductsProps extends ComponentWithPermissions {
    setData: React.Dispatch<React.SetStateAction<ProductExtendedTyShortDict>>
    data?: ProductExtendedTyShortDict
}

const RenderRelatedProducts: React.FC<RenderRelatedProductsProps> = ({ data, ...rest }) => {
    const renderThis: JSX.Element[] = []
    if (data) {
        for (const key in data) {
            const element = data[key]
            renderThis.push(<SingleRelatedProduct product={element} key={element.id} reference={element.reference} {...rest} />)
        }
    }
    return (
        <div className='my-30 container_related_products'>
            {renderThis}
        </div>
    )
}
type SingleRelatedProductProps = SingleRelatedProductLogicProps & ComponentWithPermissions & {
    reference?: string
}

const SingleRelatedProduct: React.FC<SingleRelatedProductProps> = ({ product, reference, setData, userPermissions }) => {
    const { deleteSingleRelatedProduct } = useSingleRelatedProductLogic({ setData, product })
    const productImg = product?.['ProductImages.url'] || product.url
    const productName = product?.['ProductLangs.name'] || product.name
    return (
        <div className='flex items-center mt-14'>
            <img src={productImg || '/no-image-small.png'} alt={productName} className='related_product_img' />
            <span className='text-green-00 font-medium'>
                {reference}
            </span>
            <span className='mx-5 text-green-00'>-</span>
            <span>
                {productName}
            </span>
            <PermissionChecker permission='delete' userPermissions={userPermissions}>
                <TwinIcon icon={faTrashCan} className='cursor-pointer ml-30' onClick={deleteSingleRelatedProduct} />
            </PermissionChecker>
        </div>
    )
}

interface SingleRelatedProductLogicProps {
    setData: React.Dispatch<React.SetStateAction<ProductExtendedTyShortDict>>
    product: ProductExtendedTyShort
}

const useSingleRelatedProductLogic = ({ setData, product }: SingleRelatedProductLogicProps) => {
    const deleteSingleRelatedProduct = async () => {
        setData((oldData) => {
            const copy = JSON.parse(JSON.stringify(oldData))
            delete copy[product.id]
            return { ...copy }
        })
        addAntiUnsaveElement(['relatedProduct', {}])
    }
    return { deleteSingleRelatedProduct }
}

export default RelatedProducts